import { useState, useEffect, useCallback } from 'react';
import { History, User, Shield, Plus, Edit, Trash2, Key, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';

const ACTION_LABEL = {
  create: { label: 'Criou', icon: Plus, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  update: { label: 'Editou', icon: Edit, color: 'text-electric-blue', bg: 'bg-electric-blue/10' },
  delete: { label: 'Excluiu', icon: Trash2, color: 'text-red-400', bg: 'bg-red-500/10' },
  reset_password: { label: 'Redefiniu senha de', icon: Key, color: 'text-amber-400', bg: 'bg-amber-500/10' },
};

const ENTITY_LABEL = {
  cargo: 'cargo',
  usuario: 'usuário',
};

const FIELD_LABEL = {
  nome: 'Nome',
  descricao: 'Descrição',
  cor: 'Cor',
  ativo: 'Ativo',
  permissoes: 'Permissões',
  role: 'Nível',
  cargo_id: 'Cargo',
};

function formatValue(field, value) {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return `${value.length} item(ns)`;
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'agora mesmo';
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} dia(s) atrás`;
  return d.toLocaleString('pt-BR');
}

function Auditoria() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity_type: 'all', action: 'all' });
  const [page, setPage] = useState(0);
  const [cargosMap, setCargosMap] = useState({});
  const pageSize = 30;

  useEffect(() => {
    api.get('/cargos')
      .then(({ data }) => setCargosMap(Object.fromEntries(data.map((c) => [c.id, c.nome]))))
      .catch(() => {});
  }, []);

  const resolveFieldValue = (field, value) => {
    if (value && field === 'cargo_id' && cargosMap[value]) return cargosMap[value];
    return formatValue(field, value);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: page * pageSize };
      if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
      if (filters.action !== 'all') params.action = filters.action;
      const { data } = await api.get('/audit-logs', { params });
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-4 lg:p-8" data-testid="auditoria-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-2">Auditoria</h1>
          <p className="text-slate-500 text-sm">Histórico de alterações em cargos, usuários e permissões</p>
        </div>
        <Button onClick={() => { setPage(0); load(); }} variant="ghost" className="text-electric-blue hover:bg-electric-blue/10" data-testid="refresh-logs-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">Tipo</label>
          <Select value={filters.entity_type} onValueChange={(v) => { setPage(0); setFilters((f) => ({ ...f, entity_type: v })); }}>
            <SelectTrigger className="bg-carbon border-white/10 text-white" data-testid="filter-entity-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-carbon border-white/10 text-white">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="cargo">Cargos</SelectItem>
              <SelectItem value="usuario">Usuários</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs uppercase tracking-wider text-slate-400 mb-1 block">Ação</label>
          <Select value={filters.action} onValueChange={(v) => { setPage(0); setFilters((f) => ({ ...f, action: v })); }}>
            <SelectTrigger className="bg-carbon border-white/10 text-white" data-testid="filter-action">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-carbon border-white/10 text-white">
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="create">Criação</SelectItem>
              <SelectItem value="update">Edição</SelectItem>
              <SelectItem value="delete">Exclusão</SelectItem>
              <SelectItem value="reset_password">Reset de senha</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-slate-400 py-8 text-center">Carregando...</div>
      ) : logs.length === 0 ? (
        <div className="bg-carbon border border-white/5 rounded-md p-12 text-center">
          <History className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">Nenhum evento de auditoria encontrado</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => {
            const meta = ACTION_LABEL[log.action] || { label: log.action, icon: History, color: 'text-slate-400', bg: 'bg-slate-500/10' };
            const Icon = meta.icon;
            const EntityIcon = log.entity_type === 'cargo' ? Shield : User;
            return (
              <div
                key={log.id}
                className="bg-carbon border border-white/5 rounded-md p-4 hover:border-white/20 transition-colors"
                data-testid={`log-${log.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${meta.bg} ${meta.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <div className="text-sm text-white flex items-center flex-wrap gap-1.5">
                        <span className="font-semibold">{log.actor_nome || log.actor_username}</span>
                        <span className="text-slate-400">{meta.label}</span>
                        <EntityIcon className="w-3.5 h-3.5 text-slate-500" />
                        <span className="text-slate-400">{ENTITY_LABEL[log.entity_type] || log.entity_type}</span>
                        {log.entity_name && <span className="text-electric-blue font-medium">"{log.entity_name}"</span>}
                      </div>
                      <span className="text-xs text-slate-500 flex-shrink-0" title={new Date(log.timestamp).toLocaleString('pt-BR')}>
                        {timeAgo(log.timestamp)}
                      </span>
                    </div>

                    {/* Mudanças */}
                    {log.changes && Object.keys(log.changes).length > 0 && (
                      <div className="mt-3 space-y-1 pl-0">
                        {Object.entries(log.changes).map(([field, { before, after }]) => (
                          <div key={field} className="text-xs text-slate-400 flex flex-wrap items-center gap-1.5">
                            <span className="uppercase tracking-wide text-slate-500">{FIELD_LABEL[field] || field}:</span>
                            <span className="line-through opacity-60">{resolveFieldValue(field, before)}</span>
                            <span className="text-slate-600">→</span>
                            <span className="text-white">{resolveFieldValue(field, after)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {total > pageSize && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/5">
          <span className="text-xs text-slate-500">
            Página {page + 1} de {totalPages} · {total} evento(s)
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="text-slate-300"
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="text-slate-300"
              data-testid="next-page"
            >
              Próxima <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Auditoria;
